/* El teléfono de la planilla, convertido en un enlace de WhatsApp.
 *
 * POR QUÉ ES UN MÓDULO Y NO TRES LÍNEAS EN LA PANTALLA. El mismo teléfono se
 * va a querer tocar desde documentación, desde el padrón de gente y desde
 * donde aparezca un chofer. Copiado, el día que alguien arregle un caso raro lo
 * arregla en un lugar y en los otros dos sigue mandando el mensaje a otra
 * persona.
 *
 * Y ES PURO: entra un string, sale un string. Todo lo que puede salir mal se
 * prueba en una tabla, sin navegador.
 *
 * LA REGLA QUE MANDA: ante la duda, NO se arma el enlace.
 *
 * Un número mal armado no falla — abre un chat con alguien que no es, y el
 * mensaje se manda igual. Es el mismo criterio que `patentes.py` con el
 * `sin_patente`: contestar "no está" a un dato que existe manda a corregir algo
 * que estaba bien, pero inventar un destinatario es peor. Por eso cualquier
 * número que no termine en diez dígitos exactos se muestra como texto y sin
 * enlace: el que lo necesita lo copia.
 */

/* Los formatos que trae la planilla, todos reales:
 *
 *   11 7654 0253     móvil de Buenos Aires, área de 2
 *   2964 60 7411     Río Grande, área de 4
 *   02334 405335     con el 0 de larga distancia adelante
 *   011 15 6789 0123 con el 15, que es el prefijo viejo de móvil
 *
 * Argentina completa SIEMPRE diez dígitos entre área y abonado. Ése es el
 * ancla: lo que no da diez después de limpiar, no se pudo entender.
 */
export function normalizar(tel) {
  let n = String(tel || "").replace(/\D/g, "");
  if (!n) return null;

  // Ya viene con el código de país. El 9 de móvil se saca acá y se vuelve a
  // poner abajo: así el resultado es el mismo venga como venga.
  if (n.startsWith("54")) {
    n = n.slice(2);
    if (n.length === 11 && n.startsWith("9")) n = n.slice(1);
  } else if (n.startsWith("0")) {
    // El 0 es el prefijo de larga distancia nacional: no viaja al exterior.
    n = n.slice(1);
  }

  /* EL 15 ES UN PREFIJO VIEJO Y HAY QUE SACARLO, pero sólo donde de verdad
   * está. Va después del área, así que la posición depende de cuántos dígitos
   * mide el área — 2 (11), 3 o 4. Se prueban las tres y sólo cuenta si el
   * resultado queda en diez.
   *
   * Buscarlo en cualquier lado sería peor que no buscarlo: hay abonados que
   * empiezan con 15 y les estaríamos comiendo dos dígitos.
   *
   * Son DOCE y no once: el país completa diez entre área y abonado, y el 15
   * suma dos. `11 · 15 · 6789 0123` son doce. */
  if (n.length === 12) {
    for (const area of [2, 3, 4]) {
      if (n.slice(area, area + 2) === "15") {
        n = n.slice(0, area) + n.slice(area + 2);
        break;
      }
    }
  }

  /* DIEZ DÍGITOS QUE ARRANCAN EN 15 NO SON UN NÚMERO, SON UN 15 SIN ÁREA.
   *
   * NINGUNA característica argentina empieza con 15: la única que arranca con
   * 1 es el 11 de Buenos Aires, y el resto son 2xx, 3xx o de cuatro dígitos.
   * Así que `15 2708 1126` no es «área 15, abonado 27081126»: es un móvil
   * escrito con el prefijo viejo y SIN la característica, que es un dato que
   * no está y no se puede adivinar — el mismo `15-2708-1126` es de Pilar en
   * una fila y de Capital en la siguiente.
   *
   * Sin esta línea el número igual daba diez, se le pegaba el 549 y salía un
   * enlace que abre el chat equivocado. **Un enlace que anda mal es peor que
   * no tener enlace**: el que no anda se ve y se corrige; éste manda un
   * mensaje a un desconocido y nadie se entera. Son 81 de los 236 teléfonos
   * de la planilla de RRHH y 4 de los de documentación.
   *
   * El texto se sigue mostrando: quien llama sabe leerlo y sabe de qué zona
   * es esa persona. Lo que no se hace es fabricar el atajo. */
  if (n.length === 10 && n.startsWith("15")) return null;

  // Acá o son diez o no se entendió. El 9 es lo que WhatsApp necesita para un
  // móvil argentino; sin él el chat no abre.
  return n.length === 10 ? "549" + n : null;
}

/* El enlace, o `null` si el número no se pudo entender. Quien llama muestra el
 * texto igual: el dato sigue estando, lo que falta es el atajo.
 *
 * ── `api.whatsapp.com/send`, EN LA COMPU Y EN EL CELULAR ───────────────────
 *
 * `wa.me` es un redireccionador que cae en una página intermedia (dos clics
 * de más cada vez). Por eso el 25/08/2026 pasó a `web.whatsapp.com/send`, que
 * iba derecho. El 14/10/2026 ése dejó de abrir el chat como antes, e Igna
 * pidió `api.whatsapp.com/send?phone=` para todo: abre el chat en la compu y
 * en el celular, el mismo formato que usa el bot.
 *
 * El número va igual que antes —`549` + diez dígitos, sin `+` ni espacios—:
 * lo que cambia es la puerta, no lo que se manda. */
export function aWhatsapp(tel) {
  const n = normalizar(tel);
  return n ? `https://api.whatsapp.com/send?phone=${n}` : null;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * DOS FUENTES PARA EL MISMO TELÉFONO, Y CUÁL MANDA
 *
 * El número de un chofer está escrito en dos lugares y no dicen lo mismo:
 *
 *   PADRÓN    `personas.telefono`, heredado de Firestore. De 2.476 personas
 *             sólo 65 tienen un número que se pueda entender. No es que esté
 *             desactualizado: es que nunca se llenó.
 *   PLANILLA  `chofer_documentos.telefono`, la de Marcos, que se sincroniza
 *             sola a las 03:50. 214 de 224 filas con número legible.
 *
 * Cruzadas por DNI: **176 personas tienen número en la planilla y NADA en el
 * padrón**, 25 coinciden, y hay UNA sola donde los dos dicen cosas distintas.
 * En cero casos el padrón sabe algo que la planilla no.
 *
 * Por eso MANDA LA PLANILLA. No es una preferencia de estilo: es la única que
 * alguien mantiene. La regla igual contempla el caso contrario, porque el día
 * que el padrón tenga algo que la planilla no, ese número tiene que aparecer.
 *
 * ── Y LA DISCREPANCIA SE MUESTRA, NO SE RESUELVE ────────────────────────────
 *
 * Cuando los dos tienen número y no son el mismo, se muestra el de la planilla
 * Y SE AVISA que el padrón dice otro. Elegir en silencio es lo que hace que un
 * número viejo sobreviva para siempre sin que nadie sepa que había otro; y
 * quedarse callado sería peor todavía, porque el que llama y no le atienden no
 * tiene forma de saber que había un segundo número para probar.
 *
 * Misma regla que `mecanicos.parecidos()`, que `cotejo` y que
 * `movimientos.candidato()`: lo ambiguo se muestra y decide una persona.
 * ────────────────────────────────────────────────────────────────────────── */

export const FUENTES = {
  planilla: "la planilla de documentación",
  padron: "el padrón",
};

/* Qué teléfono mostrar, de dónde salió, y si hay algo que avisar.
 *
 * Devuelve siempre la misma forma:
 *
 *   { tel, crudo, fuente, ilegible, discrepancia }
 *
 *   tel           el normalizado listo para WhatsApp, o null
 *   crudo         lo que está escrito, tal cual, para mostrarlo
 *   fuente        "planilla" | "padron" | null
 *   ilegible      hay algo escrito pero no da diez dígitos
 *   discrepancia  el otro crudo, cuando los dos existen y no coinciden
 *
 * `ilegible` NO es lo mismo que vacío, y ésa es la distinción que más importa
 * de las tres. Un teléfono que falta se pide; uno mal tipeado se corrige, y si
 * la pantalla los dibujara igual nadie sabría que hay una celda para arreglar.
 * Hoy son 1 en la planilla y 4 en el padrón.
 */
export function elegir({ padron, planilla } = {}) {
  const p = { crudo: String(planilla || "").trim(), tel: normalizar(planilla) };
  const d = { crudo: String(padron || "").trim(), tel: normalizar(padron) };

  const vacio = {
    tel: null,
    crudo: "",
    fuente: null,
    ilegible: false,
    discrepancia: "",
  };

  // Nada escrito en ningún lado. No es un error: la mayoría del padrón no
  // maneja y nunca tuvo motivo para tener un teléfono cargado acá.
  if (!p.crudo && !d.crudo) return vacio;

  // Gana el que se entiende. Un número legible del padrón vale más que uno
  // roto de la planilla, aunque la planilla sea la fuente que se mantiene:
  // preferir lo ilegible por venir del lugar correcto deja sin enlace a
  // alguien al que sí se puede llamar.
  const gana = p.tel ? p : d.tel ? d : p.crudo ? p : d;
  const fuente = gana === p ? "planilla" : "padron";

  return {
    tel: gana.tel,
    crudo: gana.crudo,
    fuente,
    ilegible: Boolean(gana.crudo) && !gana.tel,
    // Sólo cuando los DOS se entienden y son distintos. Con uno ilegible no
    // hay discrepancia que reportar: hay una celda para corregir, y eso ya lo
    // dice `ilegible`.
    discrepancia:
      p.tel && d.tel && p.tel !== d.tel ? (gana === p ? d.crudo : p.crudo) : "",
  };
}

/* ─────────────────────────────────────────────────────────────────────────────
 * QUIÉN PUEDE TOCAR EL NÚMERO DE UN CHOFER
 *
 * Nico, 09/10, con el aviso del furgón 1552 en la mano («🔧 Pedido de chofer
 * para el taller — 👤 German Daniel Silva…»): «en este reporte y en cualquiera
 * que venga de parte de un chofer o con un chofer, que aparezca el número del
 * chofer clickeable para mandar un WhatsApp». El aviso lo manda a
 * `/taller/pedidos-choferes`, y ahí el chofer era un nombre suelto.
 *
 * El número de un chofer es un dato personal de alguien que no está mirando la
 * pantalla, y por eso tiene llave propia: `ver.telefono_chofer` (migración
 * 20260827224012), que se da de a uno, como el PIN de la tarjeta. El bot ya la
 * pide para darlo por WhatsApp (`telefono_chofer.autorizado()`); acá es la
 * misma regla del lado del hub.
 *
 * Y CON LOS PERMISOS TODAVÍA CARGANDO (`null`) NO SE MUESTRA. Muchas pantallas
 * leen `null` como «todavía no sé, dibujá» para no arrancar vacías; con esto
 * es al revés, como con `aprobar.compras`: dibujar el número medio segundo y
 * después sacarlo ya es habérselo mostrado a quien no lo tenía. Una lectura
 * que falló (`misPermisos` devuelve `[]`) también cierra.
 * ────────────────────────────────────────────────────────────────────────── */

export const PERMISO_TELEFONO = "ver.telefono_chofer";

export function puedeVerTelefono(permisos) {
  return Array.isArray(permisos) && permisos.includes(PERMISO_TELEFONO);
}

/* El enlace de WhatsApp de un chofer para quien está mirando, o `null`.
 *
 * Son las dos puertas juntas, en el orden en que se cierran: sin la llave no
 * hay enlace aunque el número sea perfecto, y con la llave tampoco si el
 * número no se entiende (`aWhatsapp` → `null`, ante la duda). Así cada
 * pantalla hace UNA pregunta y no puede olvidarse de la otra. */
export function whatsappDeChofer(numero, permisos) {
  return puedeVerTelefono(permisos) ? aWhatsapp(numero) : null;
}

/* ¿Esta conversación o este aviso es de un chofer?
 *
 * El rol viene escrito a mano en la planilla («Chofer», y algún día «chofer» o
 * «CHOFER ») y en el espejo conviven con Portería, Taller, Tráfico… El número
 * de alguien del staff no es «el número del chofer» que pidió Nico, así que
 * ahí no se arma el atajo. Ante la duda —sin rol, o un rol que no es
 * exactamente chofer— tampoco. */
export function esChofer(rol) {
  return (
    String(rol ?? "")
      .trim()
      .toLowerCase() === "chofer"
  );
}

/* Cómo se lee el número en pantalla: `+54 9 1100000000` en vez de trece
 * dígitos pegados. El área no se separa del abonado porque mide 2, 3 o 4
 * dígitos y desde el número solo no se sabe cuál; se separa lo que es seguro
 * (país y el 9 de móvil). `null` si no se entiende, como `aWhatsapp`. */
export function mostrarNumero(numero) {
  const n = normalizar(numero);
  return n ? `+54 9 ${n.slice(3)}` : null;
}
